/**
 * Teslyn's sign-in emails (decision 890): Supabase Auth's emails (sign-in links and codes, invitations, email
 * changes, the security notices) go through Supabase's Auth "Send Email" hook to Teslyn's `auth-email` Edge Function,
 * which sends them through Resend from "No Reply <organisation> powered by Teslyn" <no-reply@teslyn.au>:
 *
 * - an invitation always names the organisation that invited (`invite-user` puts its id in the new sign-in's
 *   `user_metadata.teslyn_invite_tenant_id`, read for an invitation only);
 * - a sign-in link or code names the organisation when the person belongs to exactly one (staff or client);
 * - otherwise the sender is "No Reply via Teslyn".
 *
 * With the hook on, Supabase does not use its own email templates: these words replace them, and carry the code
 * (`{{ .Token }}`) and a link. The link is NOT Supabase's `{{ .ConfirmationURL }}` (`<project>/auth/v1/verify?token=
 * <token_hash>&type=<action>&redirect_to=...`): Supabase spends the one-time token on that GET, so a mail scanner that
 * opens every link (Microsoft 365's Safe Links did, 3 Nov 2026) spends the sign-in before the person reads the email,
 * and the code, which is the same token, is dead too. Instead the link goes to Teslyn's own page with the token hash
 * (`authLinkUrl`: where the person asked from, `redirect_to`, plus `?token_hash=...&type=...`), which spends nothing
 * until a button is pressed (apps/web/src/auth/authReturn.ts; docs/specs/cloud/sign-in-security.md section 3.1).
 * This is Supabase's own guard for link scanners (auth-email-templates, "Email prefetching").
 *
 * The payload is Supabase's documented one (https://supabase.com/docs/guides/auth/auth-hooks/send-email-hook, fixture
 * tests/fixtures/supabase-auth/send-email-hook.docs-2026-10-30.json); for an email change the token hashes are
 * crossed as the documentation warns (`token_hash_new` goes with the current address and `token`, `token_hash` with
 * the new address and `token_new`).
 *
 * It imports only email-sender.ts (zero imports), so the Edge Function uses the same words
 * (tools/dev/copy-edge-shared.mjs). docs/specs/cloud/auth-email.md.
 */
import { appEmailFrom } from './email-sender.js';

/** Every `email_action_type` Supabase's Send Email hook sends (its JSON schema, 30 Oct 2026). */
export const AUTH_EMAIL_ACTIONS = [
  'signup',
  'invite',
  'magiclink',
  'recovery',
  'email_change',
  'email',
  'reauthentication',
  'password_changed_notification',
  'email_changed_notification',
  'phone_changed_notification',
  'identity_linked_notification',
  'identity_unlinked_notification',
  'mfa_factor_enrolled_notification',
  'mfa_factor_unenrolled_notification',
] as const;
export type AuthEmailAction = (typeof AUTH_EMAIL_ACTIONS)[number];

/** Where `invite-user` keeps the inviting organisation on a new sign-in (read for an invitation only). */
export const INVITE_TENANT_KEY = 'teslyn_invite_tenant_id';

/** The parts of the hook's `user` Teslyn reads. */
export interface AuthHookUser {
  readonly id: string;
  readonly email: string;
  /** The address being changed to (an email change). */
  readonly new_email?: string;
  readonly user_metadata?: Readonly<Record<string, unknown>>;
}

/** The hook's `email_data`. */
export interface AuthHookEmailData {
  readonly token: string;
  readonly token_hash: string;
  readonly redirect_to: string;
  readonly email_action_type: AuthEmailAction;
  readonly site_url: string;
  readonly token_new: string;
  readonly token_hash_new: string;
  readonly old_email?: string;
}

export interface AuthHookPayload {
  readonly user: AuthHookUser;
  readonly email_data: AuthHookEmailData;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const str = (v: unknown): string => (typeof v === 'string' ? v : '');

/** The hook's JSON body as Teslyn reads it, or why it cannot be read. */
export function parseAuthHookPayload(
  body: unknown,
): { readonly ok: true; readonly value: AuthHookPayload } | { readonly ok: false; readonly error: string } {
  if (typeof body !== 'object' || body === null) return { ok: false, error: 'The hook sent no payload.' };
  const b = body as { user?: unknown; email_data?: unknown };
  const u = (typeof b.user === 'object' && b.user !== null ? b.user : {}) as Record<string, unknown>;
  const d = (typeof b.email_data === 'object' && b.email_data !== null ? b.email_data : {}) as Record<
    string,
    unknown
  >;
  const action = str(d['email_action_type']);
  if (!(AUTH_EMAIL_ACTIONS as readonly string[]).includes(action))
    return { ok: false, error: `Teslyn does not send "${action || 'no'}" emails.` };
  const email = str(u['email']).trim();
  const newEmail = str(u['new_email']).trim();
  if (!UUID.test(str(u['id']))) return { ok: false, error: 'The hook named no sign-in.' };
  if (!EMAIL.test(email) && !(action === 'email_change' && EMAIL.test(newEmail)))
    return { ok: false, error: 'The sign-in has no email address.' };
  const meta = u['user_metadata'];
  return {
    ok: true,
    value: {
      user: {
        id: str(u['id']),
        email,
        ...(newEmail ? { new_email: newEmail } : {}),
        ...(typeof meta === 'object' && meta !== null
          ? { user_metadata: meta as Record<string, unknown> }
          : {}),
      },
      email_data: {
        token: str(d['token']),
        token_hash: str(d['token_hash']),
        redirect_to: str(d['redirect_to']),
        email_action_type: action as AuthEmailAction,
        site_url: str(d['site_url']),
        token_new: str(d['token_new']),
        token_hash_new: str(d['token_hash_new']),
        ...(str(d['old_email']) ? { old_email: str(d['old_email']) } : {}),
      },
    },
  };
}

/** The organisation an invitation is from (`invite-user`'s mark), null for any other email or without one. */
export function inviteTenantId(p: AuthHookPayload): string | null {
  if (p.email_data.email_action_type !== 'invite') return null;
  const v = p.user.user_metadata?.[INVITE_TENANT_KEY];
  return typeof v === 'string' && UUID.test(v) ? v.toLowerCase() : null;
}

/**
 * Supabase's confirmation link (`{{ .ConfirmationURL }}`): the project's `/auth/v1/verify` with the token hash, the
 * action and where to go afterwards. Supabase spends the token on this GET and sends the browser on: for a PKCE
 * sign-in (a `pkce_` token hash) with `?code=`, which only the app that asked can exchange (its code verifier);
 * otherwise with the session in the address fragment. Not put in an email any more (a link scanner's GET spends it):
 * Teslyn Field's and Teslyn Mobile's return pages open it after the person presses a button, so the apps still
 * receive the `?code=` they exchange today.
 */
export function authVerifyUrl(
  supabaseUrl: string,
  tokenHash: string,
  type: AuthEmailAction,
  redirectTo: string,
): string {
  const base = supabaseUrl.trim().replace(/\/+$/, '');
  const q = [
    `token=${encodeURIComponent(tokenHash)}`,
    `type=${encodeURIComponent(type)}`,
    ...(redirectTo ? [`redirect_to=${encodeURIComponent(redirectTo)}`] : []),
  ].join('&');
  return `${base}/auth/v1/verify?${q}`;
}

/** The query parameters the email's link carries to Teslyn's page (read by apps/web/src/auth/authReturn.ts). */
export const LINK_TOKEN_PARAM = 'token_hash';
export const LINK_TYPE_PARAM = 'type';

/**
 * Teslyn Mobile's return page (`/mobile/signed-in`, Teslyn Cloud): the phone app's own address cannot be a link in an
 * email (mail apps open http(s) links only), so the email's link opens this page on the project's Site URL, which
 * opens Supabase's link to the app once "Open Teslyn Mobile" is pressed. `to` names the app's return address, one of
 * `MOBILE_RETURN_ADDRESSES` only.
 */
export const MOBILE_RETURN_PATH = '/mobile/signed-in';
export const MOBILE_RETURN_TO_PARAM = 'to';
/**
 * The apps' return addresses (`<app id>://signed-in`, docs/specs/mobile/teslyn-mobile.md section 9): the beta and the
 * stable Android app. The same ids as `MOBILE_APP_IDS` (mobile-release.ts; checked by the test), repeated here so
 * this module stays import-free for the Edge Function.
 */
export const MOBILE_RETURN_ADDRESSES: readonly string[] = [
  'au.teslyn.mobile.beta://signed-in',
  'au.teslyn.mobile://signed-in',
];

/** Teslyn Mobile's return address `to` names, or null for anything else (never an address of someone's choosing). */
export function mobileReturnAddress(to: string | null | undefined): string | null {
  const v = (to ?? '').trim();
  return MOBILE_RETURN_ADDRESSES.includes(v) ? v : null;
}

function parseUrl(s: string): URL | null {
  try {
    return s.trim() ? new URL(s.trim()) : null;
  } catch {
    return null;
  }
}

/** An address a browser opens from an email: https, or http on this computer (local development only). */
function isWebAddress(u: URL): boolean {
  return u.protocol === 'https:' || (u.protocol === 'http:' && /^(localhost|127\.0\.0\.1)$/.test(u.hostname));
}

/**
 * The link the email carries: Teslyn's own page with the token hash, so nothing is spent until a button is pressed.
 *
 * - `redirect_to` is a web address (the Teslyn Cloud page that asked, Teslyn Field's return page): that address, with
 *   `token_hash` and `type` added to its query (its own parameters, `?tenant=`, `?page=`, `?app=`, `sb_flow_id`,
 *   stay). Supabase lets only an address on the project's redirect allow list through as `redirect_to` (its Site
 *   URL otherwise), so the link never goes to an address of someone else's choosing.
 * - `redirect_to` is Teslyn Mobile's return address: the Site URL's `/mobile/signed-in` page, with `to` naming it.
 * - anything else: the Site URL with the parameters; with no usable Site URL either, Supabase's own link
 *   (`authVerifyUrl`), so an email is never without one.
 */
export function authLinkUrl(o: {
  readonly siteUrl: string;
  readonly redirectTo: string;
  readonly supabaseUrl: string;
  readonly tokenHash: string;
  readonly type: AuthEmailAction;
}): string {
  const site = parseUrl(o.siteUrl);
  const to = parseUrl(o.redirectTo);
  let page: URL | null = null;
  if (to && isWebAddress(to)) page = to;
  else if (site && isWebAddress(site)) {
    page = site;
    const mobile = mobileReturnAddress(o.redirectTo);
    if (mobile) {
      page = new URL(MOBILE_RETURN_PATH, site.origin);
      page.searchParams.set(MOBILE_RETURN_TO_PARAM, mobile);
    }
  }
  if (!page) return authVerifyUrl(o.supabaseUrl, o.tokenHash, o.type, o.redirectTo);
  page.searchParams.set(LINK_TOKEN_PARAM, o.tokenHash);
  page.searchParams.set(LINK_TYPE_PARAM, o.type);
  return page.toString();
}

/** One sign-in email, ready for Resend. */
export interface AuthEmailMessage {
  readonly from: string;
  readonly to: string;
  readonly subject: string;
  readonly text: string;
  readonly html: string;
}

const escapeHtml = (s: string): string =>
  s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/** A paragraph of the email: words, a link with its words, or a code. */
type Part =
  | { readonly p: string }
  | { readonly link: string; readonly words: string }
  | { readonly code: string; readonly words: string };

function render(parts: readonly Part[]): { text: string; html: string } {
  const text = parts
    .map((x) => ('p' in x ? x.p : 'link' in x ? `${x.words}: ${x.link}` : `${x.words}: ${x.code}`))
    .join('\n\n');
  const html = parts
    .map((x) =>
      'p' in x
        ? `<p>${escapeHtml(x.p)}</p>`
        : 'link' in x
          ? `<p><a href="${escapeHtml(x.link)}">${escapeHtml(x.words)}</a></p>`
          : `<p>${escapeHtml(x.words)}: <strong>${escapeHtml(x.code)}</strong></p>`,
    )
    .join('\n');
  return { text, html };
}

const NOTICE_WORDS: Readonly<Record<string, string>> = {
  password_changed_notification: 'The password of your Teslyn sign-in was changed',
  email_changed_notification: 'The email address of your Teslyn sign-in was changed',
  phone_changed_notification: 'The phone number of your Teslyn sign-in was changed',
  identity_linked_notification: 'A way to sign in was added to your Teslyn sign-in',
  identity_unlinked_notification: 'A way to sign in was removed from your Teslyn sign-in',
  mfa_factor_enrolled_notification: 'An authenticator was added to your Teslyn sign-in',
  mfa_factor_unenrolled_notification: 'An authenticator was removed from your Teslyn sign-in',
};

/**
 * The email (two for a secure email change) Supabase asked for, from the organisation's sender (`organisation`:
 * its name, or null for "No Reply via Teslyn"). Every link email also gives the code, as Supabase's own templates
 * can, so a screen that asks for the code works too. The link and the code are the same one-time token: the link
 * goes to Teslyn's page (`authLinkUrl`), which spends it only when the person presses the button, so a mail
 * scanner's visit leaves the code usable.
 */
export function authEmails(
  p: AuthHookPayload,
  o: { readonly supabaseUrl: string; readonly organisation: string | null },
): AuthEmailMessage[] {
  const d = p.email_data;
  const from = appEmailFrom(o.organisation);
  const org = o.organisation?.trim() || null;
  const link = (hash: string): string =>
    authLinkUrl({
      siteUrl: d.site_url,
      redirectTo: d.redirect_to,
      supabaseUrl: o.supabaseUrl,
      tokenHash: hash,
      type: d.email_action_type,
    });
  const codePart = (token: string): Part[] =>
    token ? [{ code: token, words: 'Or, if Teslyn asks for a code, enter' }] : [];
  const ignore: Part = {
    p: 'If you did not ask for this, ignore this email: nothing changes, and the link and code stop working after an hour.',
  };
  // the page the link opens asks for a press before anything is spent (a mail scanner opening the link spends nothing)
  const press = (button: string): Part => ({
    p: `The link opens a page with a "${button}" button; press it to finish. Nothing happens until you do.`,
  });
  const mail = (to: string, subject: string, parts: Part[]): AuthEmailMessage => ({
    from,
    to,
    subject,
    ...render(parts),
  });

  switch (d.email_action_type) {
    case 'magiclink':
    case 'email':
      return [
        mail(p.user.email, org ? `Sign in to Teslyn for ${org}` : 'Sign in to Teslyn', [
          {
            p: org
              ? `Sign in to Teslyn for ${org} with the link below.`
              : 'Sign in to Teslyn with the link below.',
          },
          { link: link(d.token_hash), words: 'Sign in to Teslyn' },
          press('Sign in to Teslyn'),
          ...codePart(d.token),
          ignore,
        ]),
      ];
    case 'signup':
      return [
        mail(p.user.email, 'Confirm your email address for Teslyn', [
          { p: 'Confirm this email address to finish making your Teslyn sign-in.' },
          { link: link(d.token_hash), words: 'Confirm my email address' },
          press('Confirm my email address'),
          ...codePart(d.token),
          ignore,
        ]),
      ];
    case 'invite':
      return [
        mail(p.user.email, org ? `${org} invites you to Teslyn` : 'You are invited to Teslyn', [
          {
            p: org
              ? `${org} has invited you to Teslyn. Accept the invitation with the link below to make your sign-in.`
              : 'You have been invited to Teslyn. Accept the invitation with the link below to make your sign-in.',
          },
          { link: link(d.token_hash), words: 'Accept the invitation' },
          press('Accept the invitation'),
          ...codePart(d.token),
          {
            p: 'If you were not expecting this, ignore this email: no sign-in is made until the invitation is accepted.',
          },
        ]),
      ];
    case 'recovery':
      return [
        mail(p.user.email, 'Reset your Teslyn password', [
          { p: 'Choose a new password for your Teslyn sign-in with the link below.' },
          { link: link(d.token_hash), words: 'Choose a new password' },
          press('Sign in to Teslyn'),
          ...codePart(d.token),
          ignore,
        ]),
      ];
    case 'email_change': {
      const newEmail = p.user.new_email ?? '';
      const out: AuthEmailMessage[] = [];
      const words = newEmail
        ? `Confirm that your Teslyn sign-in changes from ${p.user.email} to ${newEmail}.`
        : 'Confirm the change of the email address of your Teslyn sign-in.';
      // the counterintuitive pairs (Supabase's documentation): token_hash_new + token for the current address
      if (d.token_hash_new && EMAIL.test(p.user.email))
        out.push(
          mail(p.user.email, 'Confirm the change of your Teslyn email address', [
            { p: words },
            { link: link(d.token_hash_new), words: 'Confirm the change' },
            press('Confirm the change'),
            ...codePart(d.token),
            ignore,
          ]),
        );
      // token_hash + token_new for the new address; with secure email change off, the one pair there is
      if (d.token_hash && EMAIL.test(newEmail))
        out.push(
          mail(newEmail, 'Confirm your new email address for Teslyn', [
            { p: words },
            { link: link(d.token_hash), words: 'Confirm my new email address' },
            press('Confirm the change'),
            ...codePart(d.token_hash_new ? d.token_new : d.token_new || d.token),
            ignore,
          ]),
        );
      return out;
    }
    case 'reauthentication':
      return [
        mail(p.user.email, 'Your Teslyn code', [
          { code: d.token, words: 'Enter this code to confirm it is you' },
          { p: 'If you did not ask for a code, ignore this email.' },
        ]),
      ];
    default: {
      const what = NOTICE_WORDS[d.email_action_type] ?? 'Your Teslyn sign-in was changed';
      return [
        mail(p.user.email, what, [
          { p: `${what}.` },
          {
            p: org
              ? `If this was not you, tell ${org}'s Teslyn administrator straight away.`
              : "If this was not you, tell your organisation's Teslyn administrator straight away.",
          },
        ]),
      ];
    }
  }
}
